export const ARCHITECTURE_VIEWS = ["overview", "network", "data", "workspaces", "delivery", "jobs"] as const;
export const ARCHITECTURE_PHASES = ["current", "transition", "target"] as const;
export const ARCHITECTURE_GROUPS = ["access", "applications", "control", "data", "workspaces", "execution", "delivery", "operations", "integrations"] as const;
export const ARCHITECTURE_EVIDENCE = ["established", "published", "local-only", "proposed", "pending", "historical"] as const;
export type ArchitectureViewName = typeof ARCHITECTURE_VIEWS[number];
export type ArchitecturePhase = typeof ARCHITECTURE_PHASES[number];
export type ArchitectureGroup = typeof ARCHITECTURE_GROUPS[number];
export type ArchitectureEvidence = typeof ARCHITECTURE_EVIDENCE[number];
export type ArchitectureKind = "component" | "capability" | "control" | "decision" | "flow";
export type ArchitectureRevision = string | number;

export interface ArchitectureSource {
  id: string;
  title: string;
  path: string;
  format: string;
  sha256: string;
  status: "primary" | "supporting" | "historical";
  asOf: string;
  enabled: boolean;
}
export interface ArchitectureSourceRef {
  sourceId: string;
  slide?: number;
  section?: string;
  notes?: string | boolean;
}
export interface ArchitecturePlacement {
  location: string;
  runtime: string;
  evidence: ArchitectureEvidence;
  boundaryId?: string;
  hostEntityId?: string;
  runtimeKind?: string;
}
export interface ArchitectureEntity {
  id: string;
  name: string;
  kind: ArchitectureKind;
  group: ArchitectureGroup;
  summary: string;
  phaseNarratives?: Record<ArchitecturePhase, string>;
  views: string[];
  parentId?: string;
  placements: Record<ArchitecturePhase, ArchitecturePlacement>;
  sourceRefs: ArchitectureSourceRef[];
  tags?: string[];
  owner?: string;
}
export interface ArchitectureRelationship {
  id: string;
  from: string;
  to: string;
  label: string;
  protocol: string;
  kind: "traffic" | "control" | "artifact" | "data" | "telemetry" | "dns";
  phases: ArchitecturePhase[];
  evidence: ArchitectureEvidence;
  phaseEvidence?: Partial<Record<ArchitecturePhase, ArchitectureEvidence>>;
  sourceRefs: ArchitectureSourceRef[];
  semantics?: "connects-to" | "hosted-on" | "mounts" | "depends-on" | "controls" | "publishes" | "observes" | "resolves-to";
  connection?: { transport?: string; port?: number; exposure?: string };
}
export interface ArchitectureBoundary {
  id: string; name: string;
  kind: "project" | "network-zone" | "host" | "host-group" | "cluster" | "namespace" | "external" | "unresolved";
  phases: ArchitecturePhase[]; evidence: ArchitectureEvidence; sourceRefs: ArchitectureSourceRef[]; parentId?: string; summary?: string;
}
export interface ArchitectureFlowPreset {
  id: string; title: string; summary: string; phase: ArchitecturePhase;
  entityIds: string[]; relationshipIds: string[];
  steps: Array<{ order: number; title: string; entityIds: string[]; relationshipIds: string[]; summary?: string }>;
  sourceRefs: ArchitectureSourceRef[];
}
export interface ArchitectureCatalog {
  schemaVersion: "juris.architecture.catalog.v1";
  catalogId: string;
  revision: ArchitectureRevision;
  asOf: string;
  title: string;
  sources: ArchitectureSource[];
  entities: ArchitectureEntity[];
  relationships: ArchitectureRelationship[];
  boundaries?: ArchitectureBoundary[];
  flowPresets?: ArchitectureFlowPreset[];
  phaseOverviews?: Array<{ phase: ArchitecturePhase; title: string; summary: string; entityIds: string[]; sourceRefs: ArchitectureSourceRef[]; evidence: ArchitectureEvidence }>;
  reviewedContent?: {
    schemaVersion: "juris.architecture.reviewed-content.v1";
    excerpts: Array<{ id: string; sourceId: string; sourceSha256: string; slide?: number; section?: string; part: "slide" | "notes" | "section"; title: string; text: string; sha256: string; phases: ArchitecturePhase[]; entityIds: string[]; evidence: ArchitectureEvidence; authority: string; qualifications?: string }>;
    coverage: Array<{ sourceId: string; slide: number; slideExcerptIds: string[]; noteExcerptIds: string[]; availability: "available" | "empty" | "excluded"; retrieval: "eligible" | "excluded"; answerTests: string[]; reason?: string }>;
  };
  coverage: Array<{
    sourceId: string;
    slide: number;
    title: string;
    disposition: string;
    entityIds: string[];
    relationshipIds: string[];
    notesReviewed: boolean;
    reason?: string;
  }>;
}
export interface ArchitectureSnapshot { catalog: ArchitectureCatalog; sha256: string }
export interface ArchitectureFilters {
  query?: string;
  kind?: string;
  group?: string;
  runtime?: string;
  evidence?: string;
}
export interface ArchitectureContext {
  catalogId: string;
  revision: ArchitectureRevision;
  sha256: string;
  view: ArchitectureViewName;
  phase: ArchitecturePhase;
  filters: ArchitectureFilters;
  selectedEntityIds: string[];
  selectedRelationshipIds: string[];
}

export const GROUP_LABELS: Record<ArchitectureGroup, string> = {
  access: "Access & identity", applications: "Applications", control: "Platform control",
  data: "Data & knowledge", workspaces: "Developer workspaces", execution: "Execution",
  delivery: "Build & delivery", operations: "Operations", integrations: "External integrations",
};
export const VIEW_LABELS: Record<ArchitectureViewName, string> = {
  overview: "Overview", network: "Network", data: "Data & AI", workspaces: "Workspaces", delivery: "Delivery", jobs: "Jobs",
};
export const EVIDENCE_LABELS: Record<ArchitectureEvidence, string> = {
  established: "Established baseline", published: "Published source", "local-only": "Local implementation",
  proposed: "Proposed", pending: "Pending verification", historical: "Historical",
};

export function createArchitectureContext(snapshot: ArchitectureSnapshot): ArchitectureContext {
  return {
    catalogId: snapshot.catalog.catalogId, revision: snapshot.catalog.revision, sha256: snapshot.sha256,
    view: "overview", phase: "target", filters: {}, selectedEntityIds: [], selectedRelationshipIds: [],
  };
}

const searchable = (value: string) => value.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();

export function enabledArchitectureEntities(catalog: ArchitectureCatalog) {
  const enabled = new Set(catalog.sources.filter((source) => source.enabled).map((source) => source.id));
  return catalog.entities.filter((entity) => entity.sourceRefs.some((ref) => enabled.has(ref.sourceId)));
}

export function projectArchitecture(catalog: ArchitectureCatalog, context: Pick<ArchitectureContext, "view" | "phase" | "filters">, options: { allViews?: boolean } = {}) {
  const query = searchable(context.filters.query?.trim() ?? "");
  const enabledSources = new Set(catalog.sources.filter((source) => source.enabled).map((source) => source.id));
  const entities = enabledArchitectureEntities(catalog).filter((entity) => {
    if (!options.allViews && !entity.views.includes(context.view)) return false;
    const placement = entity.placements[context.phase];
    if (!placement) return false;
    const { kind, group, runtime, evidence } = context.filters;
    if (kind && entity.kind !== kind) return false;
    if (group && entity.group !== group) return false;
    if (runtime && placement.runtime !== runtime) return false;
    if (evidence && placement.evidence !== evidence) return false;
    return !query || searchable([entity.name, entity.summary, entity.id, placement.location, placement.runtime, ...(entity.tags ?? [])].join(" ")).includes(query);
  });
  const ids = new Set(entities.map((entity) => entity.id));
  const relationships = catalog.relationships.filter((edge) =>
    edge.phases.includes(context.phase) && ids.has(edge.from) && ids.has(edge.to) && edge.sourceRefs.some((ref) => enabledSources.has(ref.sourceId)),
  );
  return { entities, relationships };
}

export function normalizeArchitectureContext(snapshot: ArchitectureSnapshot, input: ArchitectureContext): ArchitectureContext {
  const base = createArchitectureContext(snapshot);
  const context = {
    ...base,
    view: ARCHITECTURE_VIEWS.includes(input.view) ? input.view : base.view,
    phase: ARCHITECTURE_PHASES.includes(input.phase) ? input.phase : base.phase,
    filters: Object.fromEntries(Object.entries(input.filters ?? {}).filter(([key, value]) =>
      ["query", "kind", "group", "runtime", "evidence"].includes(key) && typeof value === "string" && value.length > 0,
    ).map(([key, value]) => [key, String(value).slice(0, 240)])),
  };
  // Selection belongs to the catalog, not the current picker query or area.
  const enabledSources = new Set(snapshot.catalog.sources.filter((source) => source.enabled).map((source) => source.id));
  const entityIds = new Set(enabledArchitectureEntities(snapshot.catalog).map((entity) => entity.id));
  const edgeIds = new Set(snapshot.catalog.relationships.filter((edge) => edge.phases.includes(context.phase) && entityIds.has(edge.from) && entityIds.has(edge.to) && edge.sourceRefs.some((ref) => enabledSources.has(ref.sourceId))).map((edge) => edge.id));
  return {
    ...context,
    selectedEntityIds: [...new Set(input.selectedEntityIds ?? [])].filter((id) => entityIds.has(id)).slice(0, 15),
    selectedRelationshipIds: [...new Set(input.selectedRelationshipIds ?? [])].filter((id) => edgeIds.has(id)).slice(0, 30),
  };
}

export function architectureFilterOptions(catalog: ArchitectureCatalog, view: ArchitectureViewName, phase: ArchitecturePhase, allViews = false) {
  const entities = enabledArchitectureEntities(catalog).filter((entity) => allViews || entity.views.includes(view));
  const unique = (values: string[]) => [...new Set(values)].filter(Boolean).sort();
  return {
    kinds: unique(entities.map((entity) => entity.kind)),
    groups: ARCHITECTURE_GROUPS.filter((group) => entities.some((entity) => entity.group === group)),
    runtimes: unique(entities.map((entity) => entity.placements[phase]?.runtime ?? "")),
    evidence: ARCHITECTURE_EVIDENCE.filter((evidence) => entities.some((entity) => entity.placements[phase]?.evidence === evidence)),
  };
}

export function architectureAncestors(catalog: ArchitectureCatalog, entity: ArchitectureEntity) {
  const ancestors: ArchitectureEntity[] = [];
  const visited = new Set([entity.id]);
  let parentId = entity.parentId;
  while (parentId && !visited.has(parentId)) {
    visited.add(parentId);
    const parent = catalog.entities.find((candidate) => candidate.id === parentId);
    if (!parent) break;
    ancestors.push(parent);
    parentId = parent.parentId;
  }
  return ancestors;
}

export function toggleArchitectureSelection(snapshot: ArchitectureSnapshot, context: ArchitectureContext, entityId: string) {
  const selected = new Set(context.selectedEntityIds);
  if (selected.has(entityId)) selected.delete(entityId);
  else if (selected.size < 15) selected.add(entityId);
  return normalizeArchitectureContext(snapshot, { ...context, selectedEntityIds: [...selected] });
}

export function architectureSelection(snapshot: ArchitectureSnapshot, context: ArchitectureContext) {
  const byId = new Map(enabledArchitectureEntities(snapshot.catalog).map((entity) => [entity.id, entity]));
  return context.selectedEntityIds.flatMap((id) => byId.has(id) ? [byId.get(id)!] : []);
}

export function sourceReferenceLabel(ref: ArchitectureSourceRef) {
  return [ref.slide != null ? `Slide ${ref.slide}` : "", ref.section, ref.notes ? "Includes speaker notes" : ""].filter(Boolean).join(" · ");
}

export function architectureQuestion(context: ArchitectureContext, entity?: ArchitectureEntity, edge?: ArchitectureRelationship) {
  const phase = context.phase;
  if (edge) return `Explain ${edge.label} between these components in the ${phase} architecture. Describe the protocol, evidence, and remaining decisions.`;
  if (entity) return `Explain ${entity.name} in the ${phase} architecture. What connects to it, what changes during migration, and which sources support it?`;
  return `Explain the ${VIEW_LABELS[context.view].toLowerCase()} view of the ${phase} architecture and its main evidence gaps.`;
}
